// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ApplicationStateBadge,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  Avatar,
  FormField,
  Input,
  KycStatusBadge,
  Select,
  Skeleton,
  Textarea,
  UserStatusBadge,
  formatDateTimeVn,
  formatVnd,
  useToast,
} from "@chotca/ui";
import {
  Building2,
  Laptop,
  Lock,
  LogOut,
  Pencil,
  Smartphone,
  Tablet,
  Unlock,
} from "lucide-react";
import {
  ACCOUNT_TYPE_LABEL_VI,
  AVAILABLE_FROM,
  AVAILABLE_FROM_LABEL_VI,
  EDUCATION_LEVELS,
  EDUCATION_LEVEL_LABEL_VI,
  EMPLOYMENT_TYPE_LABEL_VI,
  EMPLOYMENT_TYPES,
  ORG_ROLE_LABEL_VI,
  PROVINCE_CODES,
  PROVINCE_NAME_VI,
  SALARY_TYPE_LABEL_VI,
  WORKER_POSITION_LABEL_VI,
  WORKER_POSITIONS,
  type ApplicationState,
  type AvailableFrom,
  type EducationLevel,
  type EmploymentType,
  type OrgRole,
  type ProvinceCode,
  type SalaryType,
  type WorkerPosition,
} from "@chotca/config";
import { Plus, Trash2 } from "lucide-react";
import {
  getSdk,
  useAdminActivityLog,
  useAdminAssignableShadowOrgs,
  useAdminGrantOrgToUser,
  useAdminUserSessions,
  useRevokeAdminUserSession,
  useRevokeAllAdminUserSessions,
} from "@chotca/api-client";
import {
  ActivityLogTable,
  type ActivityRow,
} from "@/components/activity-log-table";

export function AccountDetailClient({ userId }: { userId: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [confirmKind, setConfirmKind] = React.useState<
    "suspend" | "activate" | null
  >(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [grantOpen, setGrantOpen] = React.useState(false);

  const userQ = useQuery({
    queryKey: ["admin", "user-detail", userId],
    queryFn: async () => getSdk().admin.getUser(userId),
  });

  function refresh() {
    qc.invalidateQueries({ queryKey: ["admin", "user-detail", userId] });
    qc.invalidateQueries({ queryKey: ["admin", "users"] });
  }

  const suspend = useMutation({
    mutationFn: () => getSdk().admin.suspendUser(userId),
    onSuccess: () => {
      refresh();
      toast({ tone: "success", title: "Đã khoá tài khoản" });
      setConfirmKind(null);
    },
    onError: () => toast({ tone: "error", title: "Không khoá được" }),
  });
  const activate = useMutation({
    mutationFn: () => getSdk().admin.activateUser(userId),
    onSuccess: () => {
      refresh();
      toast({ tone: "success", title: "Đã mở khoá" });
      setConfirmKind(null);
    },
  });
  if (userQ.isLoading) return <Skeleton className="h-96 rounded-2xl" />;
  if (userQ.isError || !userQ.data) {
    return (
      <p className="text-center text-sm text-ash">
        Không tìm thấy người dùng này.
      </p>
    );
  }
  const u = userQ.data;
  const wp = u.workerProfile ?? null;
  // Eligible to receive a shadow org: an employer account not yet tied to any
  // org. Every org owner has an active OWNER membership (org-create + claim both
  // make one), so "no active membership" reliably means "owns no business".
  const canGrantOrg =
    u.accountType === "EMPLOYER" && u.memberships.length === 0;

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <Link to="/accounts" className="text-xs text-ash hover:text-ink">
          ← Tài khoản
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <Avatar
              name={wp?.fullName ?? u.email ?? "Người dùng"}
              src={wp?.avatarUrl}
              size="lg"
              tone="ink"
              className="h-16 w-16 text-lg"
            />
            <div>
              <h1 className="flex flex-wrap items-center gap-2 text-2xl font-bold text-ink">
                {wp?.fullName ?? u.email ?? "Người dùng"}
                <span className="rounded-full bg-mist px-2 py-0.5 text-[11px] font-bold uppercase text-ink">
                  {ACCOUNT_TYPE_LABEL_VI[u.accountType]}
                </span>
                <UserStatusBadge status={u.status} />
                {wp ? <KycStatusBadge status={wp.kycStatus} /> : null}
              </h1>
              <p className="text-xs font-mono text-ash">{u.id}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {canGrantOrg ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setGrantOpen(true)}
              >
                <Building2 size={14} className="mr-1" /> Trao doanh nghiệp
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil size={14} className="mr-1" /> Sửa hồ sơ
            </Button>
            {u.status === "ACTIVE" ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmKind("suspend")}
                className="text-rose-700 hover:bg-rose-50"
              >
                <Lock size={14} className="mr-1" /> Khoá
              </Button>
            ) : (
              <Button size="sm" onClick={() => setConfirmKind("activate")}>
                <Unlock size={14} className="mr-1" /> Mở khoá
              </Button>
            )}
          </div>
        </div>
      </header>

      <section className="grid gap-3 md:grid-cols-3">
        <Stat label="Email" value={u.email ?? "—"} mono />
        <Stat label="Số điện thoại" value={u.rawPhone ?? "—"} mono />
        <Stat
          label="Email xác minh"
          value={
            u.emailVerifiedAt
              ? formatDateTimeVn(new Date(u.emailVerifiedAt))
              : "Chưa"
          }
        />
        <Stat
          label="Last active"
          value={
            u.lastActiveAt ? formatDateTimeVn(new Date(u.lastActiveAt)) : "—"
          }
        />
        <Stat label="Tạo lúc" value={formatDateTimeVn(new Date(u.createdAt))} />
        <Stat
          label="Đánh giá"
          value={
            u.ratingAvg !== null
              ? `⭐ ${u.ratingAvg.toFixed(1)} · ${u.ratingCount} lượt`
              : "Chưa có"
          }
        />
      </section>

      {wp ? <WorkerProfileSection wp={wp} /> : null}

      <ApplicationsSection applications={u.recentApplications} />

      {u.memberships.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-sm font-bold uppercase text-ash">
            Là thành viên doanh nghiệp
          </h2>
          <ul className="divide-y divide-smoke rounded-2xl border border-smoke bg-paper">
            {u.memberships.map((m) => (
              <li
                key={m.orgId}
                className="flex items-center justify-between px-4 py-3 text-sm"
              >
                <Link
                  to={`/orgs/${m.orgId}`}
                  className="font-semibold text-ink hover:underline"
                >
                  {m.orgName}
                </Link>
                <Badge>{ORG_ROLE_LABEL_VI[m.role as OrgRole] ?? m.role}</Badge>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <ActiveSessionsSection userId={userId} />

      <AccountActivitySection userId={userId} />

      <ConfirmDialog
        open={confirmKind === "suspend"}
        onClose={() => setConfirmKind(null)}
        title="Khoá tài khoản?"
        description="Người dùng sẽ không đăng nhập được."
        confirmLabel="Khoá"
        destructive
        loading={suspend.isPending}
        onConfirm={() => suspend.mutate()}
      />
      <ConfirmDialog
        open={confirmKind === "activate"}
        onClose={() => setConfirmKind(null)}
        title="Mở khoá tài khoản?"
        confirmLabel="Mở khoá"
        loading={activate.isPending}
        onConfirm={() => activate.mutate()}
      />
      <WorkerEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        userId={userId}
        wp={wp}
        onSuccess={() => {
          refresh();
          setEditOpen(false);
        }}
      />
      {canGrantOrg ? (
        <GrantOrgDialog
          open={grantOpen}
          onClose={() => setGrantOpen(false)}
          userId={userId}
          userEmail={u.email ?? null}
          onSuccess={() => {
            refresh();
            setGrantOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

// Pick an unclaimed shadow org ("doanh nghiệp tạm") and hand it to this employer
// account. An inline searchable list (not FilterCombobox) because the dialog
// body is `overflow-y-auto` and would clip a popover dropdown.
function GrantOrgDialog({
  open,
  onClose,
  userId,
  userEmail,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string | null;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [selectedId, setSelectedId] = React.useState("");

  // Debounce the keystrokes → server search (mirrors the postings org filter).
  React.useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset state every time the dialog opens.
  React.useEffect(() => {
    if (!open) return;
    setSearchInput("");
    setSearch("");
    setSelectedId("");
  }, [open]);

  const orgsQ = useAdminAssignableShadowOrgs({ search: search || undefined });
  const items = orgsQ.data?.items ?? [];

  const grant = useAdminGrantOrgToUser(userId);

  function onConfirm() {
    if (!selectedId) return;
    grant.mutate(selectedId, {
      onSuccess: (res) => {
        toast({
          tone: "success",
          title: `Đã trao “${res.orgName}” cho tài khoản này`,
        });
        onSuccess();
      },
      onError: (e) =>
        toast({
          tone: "error",
          title: e instanceof Error ? e.message : "Trao doanh nghiệp thất bại",
        }),
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Trao doanh nghiệp tạm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={grant.isPending}>
            Huỷ
          </Button>
          <Button
            onClick={onConfirm}
            loading={grant.isPending}
            disabled={!selectedId}
          >
            Trao quyền chủ
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-ash">
          Chọn một doanh nghiệp tạm (do admin tạo, chưa có chủ) để trao cho
          {userEmail ? (
            <span className="font-medium text-ink"> {userEmail}</span>
          ) : (
            " tài khoản này"
          )}
          . Sau khi trao, doanh nghiệp sẽ hết là tạm và tài khoản trở thành chủ
          sở hữu.
        </p>
        <Input
          placeholder="Tìm theo tên quán..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <div className="max-h-72 overflow-y-auto rounded-2xl border border-smoke bg-paper">
          {orgsQ.isLoading ? (
            <p className="px-4 py-6 text-center text-sm text-ash">
              Đang tải...
            </p>
          ) : items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-ash">
              Không có doanh nghiệp tạm nào phù hợp.
            </p>
          ) : (
            <ul className="divide-y divide-smoke">
              {items.map((o) => {
                const selected = o.orgId === selectedId;
                const place = [o.district, o.city].filter(Boolean).join(", ");
                return (
                  <li key={o.orgId}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(o.orgId)}
                      className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${
                        selected ? "bg-lime/10" : "hover:bg-mist"
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-ink">
                          {o.name}
                        </p>
                        <p className="truncate text-xs text-ash">
                          {[place, `${o.postingCount} tin`]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      </div>
                      {selected ? (
                        <span className="shrink-0 text-xs font-bold text-lime-dark">
                          ✓ Đã chọn
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Dialog>
  );
}

type SessionItem = Awaited<
  ReturnType<ReturnType<typeof getSdk>["admin"]["listUserSessions"]>
>["items"][number];

function SessionDeviceIcon({ deviceType }: { deviceType: string }) {
  const Icon =
    deviceType === "mobile"
      ? Smartphone
      : deviceType === "tablet"
        ? Tablet
        : Laptop;
  return <Icon size={18} className="shrink-0 text-ash" />;
}

function ActiveSessionsSection({ userId }: { userId: string }) {
  const { toast } = useToast();
  const sessionsQ = useAdminUserSessions(userId);
  const revokeOne = useRevokeAdminUserSession(userId);
  const revokeAll = useRevokeAllAdminUserSessions(userId);
  const [confirmAll, setConfirmAll] = React.useState(false);
  const [revokingId, setRevokingId] = React.useState<string | null>(null);

  const items = (sessionsQ.data?.items ?? []) as SessionItem[];

  function handleRevoke(id: string) {
    setRevokingId(id);
    revokeOne.mutate(id, {
      onSuccess: () =>
        toast({ tone: "success", title: "Đã thu hồi phiên đăng nhập" }),
      onError: () => toast({ tone: "error", title: "Không thu hồi được" }),
      onSettled: () => setRevokingId(null),
    });
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-bold uppercase text-ash">
          Phiên đăng nhập đang hoạt động
        </h2>
        {items.length > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-rose-700 hover:bg-rose-50"
            onClick={() => setConfirmAll(true)}
          >
            <LogOut size={14} className="mr-1" /> Đăng xuất tất cả
          </Button>
        ) : null}
      </div>

      {sessionsQ.isLoading ? (
        <Skeleton className="h-24 rounded-2xl" />
      ) : items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-smoke bg-paper p-6 text-center text-sm text-ash">
          Không có thiết bị nào đang đăng nhập.
        </p>
      ) : (
        <ul className="divide-y divide-smoke rounded-2xl border border-smoke bg-paper">
          {items.map((s) => {
            const deviceLabel =
              [s.client, s.os].filter(Boolean).join(" · ") ||
              "Thiết bị không xác định";
            return (
              <li
                key={s.id}
                className="flex items-center justify-between gap-3 px-4 py-3"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <SessionDeviceIcon deviceType={s.deviceType} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-ink">
                      {deviceLabel}
                    </p>
                    <p className="truncate text-xs text-ash">
                      Hoạt động gần nhất:{" "}
                      {formatDateTimeVn(new Date(s.lastSeenAt))}
                      {s.ip ? ` · ${s.ip}` : ""}
                    </p>
                    <p className="truncate text-[11px] text-ash">
                      Đăng nhập: {formatDateTimeVn(new Date(s.createdAt))}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-rose-700 hover:bg-rose-50"
                  onClick={() => handleRevoke(s.id)}
                  disabled={revokeOne.isPending && revokingId === s.id}
                >
                  Thu hồi
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        title="Đăng xuất tất cả thiết bị?"
        description="Mọi phiên đăng nhập sẽ bị thu hồi. Người dùng phải đăng nhập lại."
        confirmLabel="Đăng xuất tất cả"
        destructive
        loading={revokeAll.isPending}
        onConfirm={() =>
          revokeAll.mutate(undefined, {
            onSuccess: (res) => {
              toast({
                tone: "success",
                title: `Đã thu hồi ${res.revoked} phiên`,
              });
              setConfirmAll(false);
            },
            onError: () => toast({ tone: "error", title: "Có lỗi xảy ra" }),
          })
        }
      />
    </section>
  );
}

// Per-user audit feed. Scoped server-side to actions this account performed
// (actorUserId), so the actor column is redundant and hidden. Pagination is
// kept in local state — this is a secondary panel, not a URL-addressable view.
function AccountActivitySection({ userId }: { userId: string }) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const query = useAdminActivityLog({
    actorUserId: userId,
    page,
    limit: pageSize,
  });
  const rows = (query.data?.items ?? []) as ActivityRow[];

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-bold uppercase text-ash">
        Hoạt động gần đây
      </h2>
      <ActivityLogTable
        rows={rows}
        isLoading={query.isLoading}
        isFetching={query.isFetching}
        page={page}
        pageSize={pageSize}
        total={query.data?.total}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        hideActor
        emptyText="Chưa có hoạt động nào."
      />
    </section>
  );
}

function Stat({
  label,
  value,
  mono,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border p-4 ${
        highlight ? "border-lime/50 bg-lime/10" : "border-smoke bg-paper"
      }`}
    >
      <p className="text-xs uppercase text-ash">{label}</p>
      <p
        className={`mt-1 text-sm font-medium text-ink ${mono ? "font-mono" : ""}`}
      >
        {value}
      </p>
    </div>
  );
}

type AppSummary = Awaited<
  ReturnType<ReturnType<typeof getSdk>["admin"]["getUser"]>
>["recentApplications"][number];

// "Đang nộp" = the application is still live in the funnel (the worker is
// waiting on the employer or on an interview). The rest are terminal outcomes.
const ACTIVE_APP_STATES = new Set<ApplicationState>([
  "APPLIED",
  "INVITED",
  "ACCEPTED",
  "COMMITTED",
]);

function ApplicationsSection({ applications }: { applications: AppSummary[] }) {
  const active = applications.filter((a) =>
    ACTIVE_APP_STATES.has(a.state as ApplicationState),
  );
  const past = applications.filter(
    (a) => !ACTIVE_APP_STATES.has(a.state as ApplicationState),
  );

  return (
    <section className="space-y-4">
      <ApplicationGroup
        title="Đơn đang nộp"
        emptyText="Không có đơn nào đang chờ xử lý."
        items={active}
      />
      {past.length > 0 ? (
        <ApplicationGroup title="Đơn đã kết thúc" items={past} />
      ) : null}
    </section>
  );
}

function ApplicationGroup({
  title,
  emptyText,
  items,
}: {
  title: string;
  emptyText?: string;
  items: AppSummary[];
}) {
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-bold uppercase text-ash">{title}</h2>
      {items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-smoke bg-paper p-6 text-center text-sm text-ash">
          {emptyText ?? "Chưa có đơn nào."}
        </p>
      ) : (
        <ul className="divide-y divide-smoke rounded-2xl border border-smoke bg-paper">
          {items.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <Link
                  to={`/postings/${a.postingId}`}
                  className="block truncate text-sm font-semibold text-ink hover:underline"
                >
                  {a.postingTitle}
                </Link>
                <p className="truncate text-xs text-ash">
                  <Link to={`/orgs/${a.orgId}`} className="hover:underline">
                    {a.orgName}
                  </Link>{" "}
                  · Nộp {formatDateTimeVn(new Date(a.appliedAt))}
                  {a.decidedAt
                    ? ` · Xử lý ${formatDateTimeVn(new Date(a.decidedAt))}`
                    : ""}
                </p>
              </div>
              <ApplicationStateBadge state={a.state as ApplicationState} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function WorkerProfileSection({
  wp,
}: {
  wp: NonNullable<
    Awaited<
      ReturnType<ReturnType<typeof getSdk>["admin"]["getUser"]>
    >["workerProfile"]
  >;
}) {
  const rows: [string, React.ReactNode][] = [
    ["Năm sinh", wp.birthYear ?? "—"],
    ["Giới tính", wp.gender ?? "—"],
    ["Khu vực hiện tại", wp.currentDistrict ?? "—"],
    ["Quê", wp.hometown ? PROVINCE_NAME_VI[wp.hometown as ProvinceCode] : "—"],
    ["Sinh sống", wp.livingArrangement ?? "—"],
    [
      "Học vấn",
      wp.education
        ? EDUCATION_LEVEL_LABEL_VI[wp.education as EducationLevel]
        : "—",
    ],
    ["Chiều cao", wp.heightCm ? `${wp.heightCm} cm` : "—"],
    ["Cân nặng", wp.weightKg ? `${wp.weightKg} kg` : "—"],
    ["Có xe máy", wp.hasMotorbike ? "Có" : "Không"],
    ["Hình xăm thấy được", wp.hasVisibleTattoo ? "Có" : "Không"],
    [
      "Lương mong muốn",
      wp.desiredSalaryMin || wp.desiredSalaryMax
        ? `${formatVnd(Number(wp.desiredSalaryMin ?? 0))} – ${formatVnd(Number(wp.desiredSalaryMax ?? 0))} / ${wp.desiredSalaryType ? (SALARY_TYPE_LABEL_VI[wp.desiredSalaryType as SalaryType] ?? wp.desiredSalaryType) : ""}`
        : "—",
    ],
    [
      "Sẵn sàng từ",
      wp.availableFrom
        ? (AVAILABLE_FROM_LABEL_VI[wp.availableFrom as AvailableFrom] ??
          wp.availableFrom)
        : "—",
    ],
    [
      "Vị trí mong muốn",
      wp.desiredPositions.length
        ? wp.desiredPositions
            .map((p) => WORKER_POSITION_LABEL_VI[p as WorkerPosition] ?? p)
            .join(", ")
        : "—",
    ],
    [
      "Loại hợp đồng",
      wp.employmentTypes.length
        ? wp.employmentTypes
            .map((e) => EMPLOYMENT_TYPE_LABEL_VI[e as EmploymentType] ?? e)
            .join(", ")
        : "—",
    ],
    [
      "Khu vực ưu tiên",
      wp.desiredAreas.length ? wp.desiredAreas.join(", ") : "—",
    ],
    ["Kỹ năng", wp.skills.length ? wp.skills.join(", ") : "—"],
    ["Ưu thế", wp.advantages.length ? wp.advantages.join(", ") : "—"],
    [
      "Ngôn ngữ",
      wp.languages?.length
        ? wp.languages.map((l) => `${l.lang} (${l.level})`).join(", ")
        : "—",
    ],
    ["Tổng đơn", wp.totalApplications],
    ["Tổng lượt phỏng vấn", wp.totalInvites],
    ["Mức hoàn thiện", `${wp.completionPct}%`],
  ];
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-bold uppercase text-ash">
        Hồ sơ người lao động
      </h2>
      {wp.bioShort ? (
        <p className="rounded-2xl border border-smoke bg-paper p-4 text-sm italic text-ink">
          “{wp.bioShort}”
        </p>
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
        {rows.map(([k, v]) => (
          <div
            key={k as string}
            className="rounded-2xl border border-smoke bg-paper px-4 py-3"
          >
            <p className="text-[11px] uppercase text-ash">{k}</p>
            <p className="text-sm text-ink">{v}</p>
          </div>
        ))}
      </div>
      {wp.experiences.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-xs font-bold uppercase text-ash">
            Kinh nghiệm làm việc
          </h3>
          <ul className="space-y-2">
            {wp.experiences.map((e) => (
              <ExperienceCard key={e.id} e={e} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

type ExperienceItem = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getSdk>["admin"]["getUser"]>
  >["workerProfile"]
>["experiences"][number];

// Branch-linked entries carry the trusted org/branch (placeName is null), so
// the place line must read `branch` first and fall back to the free-typed
// placeName. The description is the worker's own summary — the main hiring
// signal — so it gets its own lime callout.
function ExperienceCard({ e }: { e: ExperienceItem }) {
  const place = e.branch
    ? `${e.branch.alias} · ${e.branch.orgName}`
    : (e.placeName?.trim() ?? null);

  return (
    <li className="rounded-2xl border border-smoke bg-paper p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-ink">
          {WORKER_POSITION_LABEL_VI[e.position as WorkerPosition] ?? e.position}
        </span>
        {e.current ? (
          <span className="rounded-md bg-lime px-1.5 py-0.5 text-[10px] font-bold text-ink">
            Hiện tại
          </span>
        ) : null}
        <span className="rounded-md bg-lime-light px-1.5 py-0.5 text-[11px] font-bold text-lime-dark">
          {formatExpPeriod(e)}
        </span>
      </div>
      {place ? (
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[13px] text-ink/80">
          <span>{place}</span>
          {e.branch ? (
            e.branch.kycVerified ? (
              <span className="font-bold text-lime-dark">✓ Đã xác thực</span>
            ) : null
          ) : (
            <span className="text-[11px] text-ash">(tự nhập)</span>
          )}
          {e.branch?.district ? (
            <span className="text-ash">· {e.branch.district}</span>
          ) : null}
        </p>
      ) : (
        <p className="mt-1 text-[12px] italic text-ash">
          Chưa có tên nơi làm việc
        </p>
      )}
      {e.description?.trim() ? (
        <p className="mt-2 whitespace-pre-line rounded-r-lg border-l-[3px] border-lime bg-mist px-3 py-2 text-[13px] leading-relaxed text-ink/80">
          {e.description}
        </p>
      ) : null}
    </li>
  );
}

type WP = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof getSdk>["admin"]["getUser"]>
  >["workerProfile"]
>;
type LangEntry = { lang: string; level: "BASIC" | "INTERMEDIATE" | "FLUENT" };

const NONE = "__none__";
const sel = <T extends string>(v: string) => (v === NONE ? null : (v as T));

const GENDER_OPTS = [
  { value: NONE, label: "— Chưa chọn —" },
  { value: "MALE", label: "Nam" },
  { value: "FEMALE", label: "Nữ" },
  { value: "OTHER", label: "Khác" },
];
const EDUCATION_OPTS = [
  { value: NONE, label: "— Chưa chọn —" },
  ...EDUCATION_LEVELS.map((v) => ({
    value: v,
    label: EDUCATION_LEVEL_LABEL_VI[v as EducationLevel],
  })),
];
const PROVINCE_OPTS = [
  { value: NONE, label: "— Chưa chọn —" },
  ...PROVINCE_CODES.map((v) => ({
    value: v,
    label: PROVINCE_NAME_VI[v as ProvinceCode],
  })),
];
const LIVING_OPTS = [
  { value: NONE, label: "— Chưa chọn —" },
  { value: "OWN", label: "Tự sở hữu nhà" },
  { value: "RENT", label: "Ở trọ" },
  { value: "WITH_FAMILY", label: "Ở với gia đình" },
];
const AVAILABLE_OPTS = [
  { value: NONE, label: "— Chưa chọn —" },
  ...AVAILABLE_FROM.map((v) => ({
    value: v,
    label: AVAILABLE_FROM_LABEL_VI[v as AvailableFrom],
  })),
];
const SALARY_TYPE_OPTS = [
  { value: NONE, label: "— Chưa chọn —" },
  { value: "PER_HOUR", label: "/giờ" },
  { value: "PER_SHIFT", label: "/ca" },
  { value: "PER_DAY", label: "/ngày" },
  { value: "PER_MONTH", label: "/tháng" },
];
const LANG_LEVEL_OPTS = [
  { value: "BASIC", label: "Cơ bản" },
  { value: "INTERMEDIATE", label: "Trung cấp" },
  { value: "FLUENT", label: "Thành thạo" },
];
function formatExpPeriod(e: {
  startMonth: number;
  startYear: number;
  endMonth: number | null;
  endYear: number | null;
  current: boolean;
}): string {
  const start = `${String(e.startMonth).padStart(2, "0")}/${e.startYear}`;
  if (e.current || e.endMonth == null || e.endYear == null) {
    return `${start} → Hiện tại`;
  }
  return `${start} → ${String(e.endMonth).padStart(2, "0")}/${e.endYear}`;
}

const MONTH_OPTS = Array.from({ length: 12 }, (_, i) => ({
  value: String(i + 1),
  label: `Tháng ${i + 1}`,
}));
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTS = Array.from({ length: 31 }, (_, i) => {
  const y = CURRENT_YEAR - i;
  return { value: String(y), label: String(y) };
});
const POSITION_OPTS = [
  { value: NONE, label: "— Chọn vị trí —" },
  ...WORKER_POSITIONS.map((v) => ({
    value: v,
    label: WORKER_POSITION_LABEL_VI[v as WorkerPosition],
  })),
];

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="pt-2 text-xs font-bold uppercase tracking-wider text-ash border-t border-smoke">
      {children}
    </p>
  );
}

// Optional numeric string: empty is allowed, otherwise must be an integer
// within [min, max]. Keeps the admin tool forgiving while catching typos.
const optionalIntInRange = (min: number, max: number, msg: string) =>
  z
    .string()
    .refine(
      (v) => v === "" || (/^\d+$/.test(v) && +v >= min && +v <= max),
      msg,
    );

const workerFormSchema = z.object({
  avatarUrl: z.string().trim(),
  fullName: z.string().trim().max(120, "Họ và tên tối đa 120 ký tự"),
  birthYear: optionalIntInRange(1900, CURRENT_YEAR, "Năm sinh không hợp lệ"),
  gender: z.string(),
  currentDistrict: z.string().trim().max(120, "Khu vực tối đa 120 ký tự"),
  hometown: z.string(),
  bioShort: z.string().trim().max(1000, "Giới thiệu tối đa 1000 ký tự"),
  education: z.string(),
  heightCm: optionalIntInRange(100, 250, "Chiều cao không hợp lệ"),
  weightKg: optionalIntInRange(30, 250, "Cân nặng không hợp lệ"),
  livingArrangement: z.string(),
  hasMotorbike: z.boolean(),
  hasVisibleTattoo: z.boolean(),
  availableFrom: z.string(),
  desiredSalaryMin: optionalIntInRange(0, 1_000_000_000, "Lương không hợp lệ"),
  desiredSalaryMax: optionalIntInRange(0, 1_000_000_000, "Lương không hợp lệ"),
  desiredSalaryType: z.string(),
  desiredPositions: z.array(z.custom<WorkerPosition>()),
  employmentTypes: z.array(z.custom<EmploymentType>()),
  languages: z.array(
    z.object({
      lang: z.string(),
      level: z.custom<LangEntry["level"]>(),
    }),
  ),
});
type WorkerFormValues = z.infer<typeof workerFormSchema>;

function wpToForm(wp: WP | null): WorkerFormValues {
  return {
    avatarUrl: wp?.avatarUrl ?? "",
    fullName: wp?.fullName ?? "",
    birthYear: wp?.birthYear?.toString() ?? "",
    gender: wp?.gender ?? NONE,
    currentDistrict: wp?.currentDistrict ?? "",
    hometown: wp?.hometown ?? NONE,
    bioShort: wp?.bioShort ?? "",
    education: wp?.education ?? NONE,
    heightCm: wp?.heightCm?.toString() ?? "",
    weightKg: wp?.weightKg?.toString() ?? "",
    livingArrangement: wp?.livingArrangement ?? NONE,
    hasMotorbike: wp?.hasMotorbike ?? false,
    hasVisibleTattoo: wp?.hasVisibleTattoo ?? false,
    availableFrom: wp?.availableFrom ?? NONE,
    desiredSalaryMin: wp?.desiredSalaryMin?.toString() ?? "",
    desiredSalaryMax: wp?.desiredSalaryMax?.toString() ?? "",
    desiredSalaryType: wp?.desiredSalaryType ?? NONE,
    desiredPositions: (wp?.desiredPositions ?? []) as WorkerPosition[],
    employmentTypes: (wp?.employmentTypes ?? []) as EmploymentType[],
    languages: (wp?.languages ?? []) as LangEntry[],
  };
}

const expFormSchema = z.object({
  position: z.string().refine((v) => v !== NONE, "Chọn vị trí"),
  place: z.string().trim().max(120, "Tên nơi làm việc tối đa 120 ký tự"),
  startMonth: z.string(),
  startYear: z.string(),
  endMonth: z.string(),
  endYear: z.string(),
  current: z.boolean(),
});
type ExpFormValues = z.infer<typeof expFormSchema>;

function emptyExpValues(): ExpFormValues {
  const m = String(new Date().getMonth() + 1);
  return {
    position: NONE,
    place: "",
    startMonth: m,
    startYear: String(CURRENT_YEAR),
    endMonth: m,
    endYear: String(CURRENT_YEAR),
    current: false,
  };
}

function WorkerEditDialog({
  open,
  onClose,
  userId,
  wp,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  wp: WP | null;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();

  // ── scalar fields ─────────────────────────────────────────────────���────
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<WorkerFormValues>({
    resolver: zodResolver(workerFormSchema),
    mode: "onTouched",
    defaultValues: wpToForm(wp),
  });
  const {
    fields: langFields,
    append: appendLang,
    remove: removeLang,
  } = useFieldArray({ control, name: "languages" });

  const expForm = useForm<ExpFormValues>({
    resolver: zodResolver(expFormSchema),
    mode: "onTouched",
    defaultValues: emptyExpValues(),
  });
  const expCurrent = expForm.watch("current");

  React.useEffect(() => {
    if (!open) return;
    reset(wpToForm(wp));
    expForm.reset(emptyExpValues());
  }, [open, wp, reset, expForm]);

  const update = useMutation({
    mutationFn: (v: WorkerFormValues) =>
      getSdk().admin.updateWorkerProfile(userId, {
        avatarUrl: v.avatarUrl || null,
        fullName: v.fullName || undefined,
        birthYear: v.birthYear ? parseInt(v.birthYear) : null,
        gender: sel<"MALE" | "FEMALE" | "OTHER">(v.gender),
        currentDistrict: v.currentDistrict || null,
        hometown: sel<ProvinceCode>(v.hometown),
        bioShort: v.bioShort || null,
        education: sel<EducationLevel>(v.education),
        heightCm: v.heightCm ? parseInt(v.heightCm) : null,
        weightKg: v.weightKg ? parseInt(v.weightKg) : null,
        livingArrangement: sel<"OWN" | "RENT" | "WITH_FAMILY">(
          v.livingArrangement,
        ),
        hasMotorbike: v.hasMotorbike,
        hasVisibleTattoo: v.hasVisibleTattoo,
        availableFrom: sel<
          "IMMEDIATE" | "IN_1_WEEK" | "IN_2_WEEKS" | "FLEXIBLE"
        >(v.availableFrom),
        desiredSalaryMin: v.desiredSalaryMin
          ? parseInt(v.desiredSalaryMin)
          : null,
        desiredSalaryMax: v.desiredSalaryMax
          ? parseInt(v.desiredSalaryMax)
          : null,
        desiredSalaryType: sel<
          "PER_HOUR" | "PER_SHIFT" | "PER_DAY" | "PER_MONTH"
        >(v.desiredSalaryType),
        desiredPositions: v.desiredPositions,
        employmentTypes: v.employmentTypes,
        languages: v.languages.length ? v.languages : null,
      }),
    onSuccess: () => {
      toast({ tone: "success", title: "Đã cập nhật hồ sơ" });
      onSuccess();
    },
    onError: () => toast({ tone: "error", title: "Cập nhật thất bại" }),
  });
  const onSave = handleSubmit((v) => update.mutate(v));

  const addExp = useMutation({
    mutationFn: (v: ExpFormValues) =>
      getSdk().admin.addWorkerExperience(userId, {
        position: v.position as WorkerPosition,
        startMonth: parseInt(v.startMonth, 10),
        startYear: parseInt(v.startYear, 10),
        endMonth: v.current ? null : parseInt(v.endMonth, 10),
        endYear: v.current ? null : parseInt(v.endYear, 10),
        current: v.current,
        placeName: v.place || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user-detail", userId] });
      expForm.reset(emptyExpValues());
      toast({ tone: "success", title: "Đã thêm kinh nghiệm" });
    },
    onError: (e: Error) =>
      toast({ tone: "error", title: e.message || "Thêm thất bại" }),
  });
  const onAddExp = expForm.handleSubmit((v) => addExp.mutate(v));

  const delExp = useMutation({
    mutationFn: (expId: string) =>
      getSdk().admin.deleteWorkerExperience(userId, expId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user-detail", userId] });
      toast({ tone: "success", title: "Đã xoá kinh nghiệm" });
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Sửa hồ sơ người lao động"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
            Huỷ
          </Button>
          <Button onClick={onSave} loading={update.isPending}>
            Lưu thay đổi
          </Button>
        </>
      }
    >
      <form
        onSubmit={onSave}
        noValidate
        className="max-h-[70vh] overflow-y-auto space-y-4 pr-1"
      >
        {/* ── Avatar ── */}
        <SectionLabel>Avatar</SectionLabel>
        <FormField label="URL ảnh đại diện">
          <Input placeholder="https://..." {...register("avatarUrl")} />
        </FormField>

        {/* ── Thông tin cá nhân ── */}
        <SectionLabel>Thông tin cá nhân</SectionLabel>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Họ và tên" error={errors.fullName?.message}>
            <Input
              placeholder="Họ và tên"
              invalid={!!errors.fullName}
              {...register("fullName")}
            />
          </FormField>
          <FormField label="Năm sinh" error={errors.birthYear?.message}>
            <Input
              type="number"
              placeholder="VD: 1998"
              invalid={!!errors.birthYear}
              {...register("birthYear")}
            />
          </FormField>
          <FormField label="Giới tính">
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={GENDER_OPTS}
                />
              )}
            />
          </FormField>
          <FormField
            label="Khu vực hiện tại"
            error={errors.currentDistrict?.message}
          >
            <Input
              placeholder="VD: Quận 1, TP.HCM"
              invalid={!!errors.currentDistrict}
              {...register("currentDistrict")}
            />
          </FormField>
          <FormField label="Quê quán">
            <Controller
              control={control}
              name="hometown"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={PROVINCE_OPTS}
                />
              )}
            />
          </FormField>
          <FormField label="Học vấn">
            <Controller
              control={control}
              name="education"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={EDUCATION_OPTS}
                />
              )}
            />
          </FormField>
          <FormField label="Chỗ ở">
            <Controller
              control={control}
              name="livingArrangement"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={LIVING_OPTS}
                />
              )}
            />
          </FormField>
        </div>
        <FormField label="Giới thiệu bản thân" error={errors.bioShort?.message}>
          <Textarea
            placeholder="Viết vài dòng giới thiệu..."
            rows={3}
            invalid={!!errors.bioShort}
            {...register("bioShort")}
          />
        </FormField>

        {/* ── Thể chất ── */}
        <SectionLabel>Thể chất</SectionLabel>
        <div className="grid gap-3 sm:grid-cols-3">
          <FormField label="Chiều cao (cm)" error={errors.heightCm?.message}>
            <Input
              type="number"
              placeholder="165"
              invalid={!!errors.heightCm}
              {...register("heightCm")}
            />
          </FormField>
          <FormField label="Cân nặng (kg)" error={errors.weightKg?.message}>
            <Input
              type="number"
              placeholder="55"
              invalid={!!errors.weightKg}
              {...register("weightKg")}
            />
          </FormField>
        </div>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 accent-ember"
              {...register("hasMotorbike")}
            />
            Có xe máy
          </label>
          <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 accent-ember"
              {...register("hasVisibleTattoo")}
            />
            Có hình xăm thấy được
          </label>
        </div>

        {/* ── Mong muốn việc làm ── */}
        <SectionLabel>Mong muốn việc làm</SectionLabel>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Sẵn sàng từ">
            <Controller
              control={control}
              name="availableFrom"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={AVAILABLE_OPTS}
                />
              )}
            />
          </FormField>
          <FormField label="Loại lương">
            <Controller
              control={control}
              name="desiredSalaryType"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={SALARY_TYPE_OPTS}
                />
              )}
            />
          </FormField>
          <FormField
            label="Lương tối thiểu (VND)"
            error={errors.desiredSalaryMin?.message}
          >
            <Input
              type="number"
              placeholder="5000000"
              invalid={!!errors.desiredSalaryMin}
              {...register("desiredSalaryMin")}
            />
          </FormField>
          <FormField
            label="Lương tối đa (VND)"
            error={errors.desiredSalaryMax?.message}
          >
            <Input
              type="number"
              placeholder="10000000"
              invalid={!!errors.desiredSalaryMax}
              {...register("desiredSalaryMax")}
            />
          </FormField>
        </div>
        <Controller
          control={control}
          name="desiredPositions"
          render={({ field }) => (
            <div>
              <p className="mb-2 text-xs text-ash">Vị trí mong muốn</p>
              <div className="flex flex-wrap gap-2">
                {WORKER_POSITIONS.map((pos) => (
                  <label
                    key={pos}
                    className="flex items-center gap-1.5 rounded-full border border-smoke bg-mist px-3 py-1 text-xs cursor-pointer hover:bg-smoke transition-colors has-checked:border-ember has-checked:bg-ember/10 has-checked:text-ember"
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={field.value.includes(pos as WorkerPosition)}
                      onChange={() =>
                        field.onChange(
                          field.value.includes(pos as WorkerPosition)
                            ? field.value.filter((p) => p !== pos)
                            : [...field.value, pos as WorkerPosition],
                        )
                      }
                    />
                    {WORKER_POSITION_LABEL_VI[pos as WorkerPosition]}
                  </label>
                ))}
              </div>
            </div>
          )}
        />
        <Controller
          control={control}
          name="employmentTypes"
          render={({ field }) => (
            <div>
              <p className="mb-2 text-xs text-ash">Loại hợp đồng</p>
              <div className="flex flex-wrap gap-2">
                {EMPLOYMENT_TYPES.map((et) => (
                  <label
                    key={et}
                    className="flex items-center gap-1.5 rounded-full border border-smoke bg-mist px-3 py-1 text-xs cursor-pointer hover:bg-smoke transition-colors has-checked:border-ember has-checked:bg-ember/10 has-checked:text-ember"
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={field.value.includes(et as EmploymentType)}
                      onChange={() =>
                        field.onChange(
                          field.value.includes(et as EmploymentType)
                            ? field.value.filter((e) => e !== et)
                            : [...field.value, et as EmploymentType],
                        )
                      }
                    />
                    {EMPLOYMENT_TYPE_LABEL_VI[et as EmploymentType]}
                  </label>
                ))}
              </div>
            </div>
          )}
        />

        {/* ── Ngôn ngữ ── */}
        <SectionLabel>Ngôn ngữ</SectionLabel>
        <div className="space-y-2">
          {langFields.map((f, i) => (
            <div key={f.id} className="flex items-center gap-2">
              <Input
                className="flex-1"
                placeholder="VD: English, 日本語"
                {...register(`languages.${i}.lang` as const)}
              />
              <div className="w-36 shrink-0">
                <Controller
                  control={control}
                  name={`languages.${i}.level` as const}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      options={LANG_LEVEL_OPTS}
                    />
                  )}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeLang(i)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => appendLang({ lang: "", level: "BASIC" })}
          >
            <Plus size={14} className="mr-1" /> Thêm ngôn ngữ
          </Button>
        </div>

        {/* ── Kinh nghiệm ── */}
        <SectionLabel>Kinh nghiệm làm việc</SectionLabel>
        {(wp?.experiences ?? []).length > 0 && (
          <ul className="divide-y divide-smoke rounded-2xl border border-smoke bg-paper">
            {(wp?.experiences ?? []).map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between px-4 py-2 text-sm"
              >
                <div>
                  <span className="font-medium">
                    {WORKER_POSITION_LABEL_VI[e.position as WorkerPosition] ??
                      e.position}
                  </span>
                  <span className="ml-2 text-xs text-ash">
                    {e.placeName ? `${e.placeName} · ` : ""}
                    {formatExpPeriod(e)}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => delExp.mutate(e.id)}
                  loading={delExp.isPending && delExp.variables === e.id}
                  className="text-rose-600 hover:bg-rose-50"
                >
                  <Trash2 size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="rounded-2xl border border-dashed border-smoke bg-mist/50 p-3 space-y-2">
          <p className="text-xs font-medium text-ash">Thêm kinh nghiệm mới</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <FormField error={expForm.formState.errors.position?.message}>
              <Controller
                control={expForm.control}
                name="position"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    options={POSITION_OPTS}
                    invalid={!!expForm.formState.errors.position}
                  />
                )}
              />
            </FormField>
            <Input
              placeholder="Tên nơi làm việc (tùy chọn)"
              {...expForm.register("place")}
            />
          </div>
          <div className="grid gap-2 sm:grid-cols-4">
            <Controller
              control={expForm.control}
              name="startMonth"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={MONTH_OPTS}
                />
              )}
            />
            <Controller
              control={expForm.control}
              name="startYear"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={YEAR_OPTS}
                />
              )}
            />
            {expCurrent ? (
              <div className="col-span-2 text-xs text-ash self-center">
                → Hiện tại
              </div>
            ) : (
              <>
                <Controller
                  control={expForm.control}
                  name="endMonth"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      options={MONTH_OPTS}
                    />
                  )}
                />
                <Controller
                  control={expForm.control}
                  name="endYear"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      options={YEAR_OPTS}
                    />
                  )}
                />
              </>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs text-ash">
            <input type="checkbox" {...expForm.register("current")} />
            Hiện đang làm
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onAddExp}
            loading={addExp.isPending}
          >
            <Plus size={14} className="mr-1" /> Thêm
          </Button>
        </div>
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
